"use client";
import { StackProps } from "@chakra-ui/react";
import { ImageFragment } from "lib/gcms";
import {
  Gcms_Button,
  Gcms_ContentPosition,
  Gcms_ThemeColor,
} from "lib/cms-typings";
import { HoverCardProps } from "@ui/components";
import { FeatureCardsSection } from "components/feature-cards-section";
import { Faqs_T } from "@ui/marketing";
import { SectionWithImage } from "./section-with-image";
import { RichTextContent } from "@graphcms/rich-text-types";
import { FeaturedRiskSection } from "./featured-risk-section";

export const flexStyles: { [key: string]: Partial<StackProps> } = {
  LEFT: {
    direction: {
      base: "column",
      lg: "row",
    },
  },
  RIGHT: {
    direction: {
      base: "column-reverse",
      lg: "row-reverse",
    },
  },
  CENTER: {
    direction: {
      base: "column",
    },
    alignItems: "center",
    justifyContent: "center",
    sx: {
      p: {
        py: {
          base: "6",
        },
      },
    },
  },
};

export function getAlign(pos?: Gcms_ContentPosition | null) {
  switch (pos) {
    case "CENTER":
      return "center";
    case "LEFT":
      return "left";
    case "RIGHT":
      return "right";
    default:
      "left";
  }
}

export type DynamicSectionFromOptions =
  | "feature-cards"
  | "section-with-image"
  | "featured-risk-tool"
  | "about";

export interface DynamicSectionProps {
  /**
   * A unique identifier for the section entry in Hygraph.
   */
  id: string;
  bg: Gcms_ThemeColor;
  color?: Gcms_ThemeColor;

  /**
   * An array of buttons displayed within the section.
   */
  buttons?: Gcms_Button[];
  /**
   * Determines the flex alignment from `flexStyles`.
   */
  contentPosition?: Gcms_ContentPosition;

  image?: ImageFragment & { alt?: string };

  /**
   * An array of cards used in `FeatureCardsSection` for displaying on desktop view.
   */
  infoCards?: HoverCardProps[];
  /**
   * An FAQ section used in `FeatureCardsSection` for displaying the same content as in the `infoCards`, for mobile view.
   */
  faqSection?: Omit<Faqs_T, "renderAnswer">;

  content?: RichTextContent;
  title?: string;
  subtitle?: string;

  /**
   * Alignment for text content from `getAlign`.
   */
  textAlignment?: Gcms_ContentPosition;
  /**
   * Whether the section uses smaller text styling.
   */
  smallText?: boolean;

  titleColor?: Gcms_ThemeColor;
  subtitleColor?: Gcms_ThemeColor;

  /**
   * Used to determine whether the `SectionTemplate` wrapper within the component has rounded corners.
   */
  rounded?: boolean;
  /**
   * Determines the section type.
   */
  from?: DynamicSectionFromOptions;
}

/**
 * `Section` dynamically renders a section component based on the `from` property.
 *
 * This component acts as a switch-based renderer, selecting the appropriate section type.
 *
 * @returns {JSX.Element} The rendered section component based on the provided `from` prop, or it will default to `SectionWithImage`.
 */
export const Section = (props: DynamicSectionProps): JSX.Element => {
  switch (props.from) {
    case "feature-cards":
      return (
        <FeatureCardsSection
          {...props}
          title={props.title as string}
          subtitle={props.subtitle as string}
          titleColor={props.titleColor as string}
          subtitleColor={props.subtitleColor as string}
          infoCards={props.infoCards as HoverCardProps[]}
          faqSection={props.faqSection as Omit<Faqs_T, "renderAnswer">}
          from="b2c"
        />
      );
    case "section-with-image":
      return <SectionWithImage {...props} />;
    case "featured-risk-tool":
    case "about":
      return <FeaturedRiskSection {...props} />;
    default:
      return <SectionWithImage {...props} />;
  }
};

export default Section;
